import {
  Add,
  Campaign,
  Cancel,
  CheckCircle,
  Delete,
  Edit,
  MoreVert,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { hostname, User } from "../../utils";
import NotificationEditorDialog from "./dialogs/NotificationEditorDialog";

interface WhitelabelNotification {
  id: string;
  title: string;
  message: string;
  notificationType: string;
  priority: string;
  publishAt: string | null;
  expiresAt: string | null;
  notificationData: any;
  createdByUid: string;
  created_date: string;
  updated_date: string;
  deleted_date: string | null;
}

export default function WhitelabelNotifications({
  whitelabelId,
  currentUser,
  getToken,
}: {
  whitelabelId: string;
  currentUser: User;
  getToken: () => Promise<string>;
}) {
  const [notifications, setNotifications] = useState<WhitelabelNotification[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<WhitelabelNotification | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] =
    useState<WhitelabelNotification | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<{
    [key: string]: HTMLElement | null;
  }>({});

  useEffect(() => {
    loadNotifications();
  }, [whitelabelId]);

  const loadNotifications = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabelId}/notifications?include_inactive=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setNotifications(response.data.notifications || []);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const handleEditNotification = (notification: WhitelabelNotification) => {
    setSelectedNotification(notification);
    setEditDialogOpen(true);
    handleCloseMenu(notification.id);
  };

  const handleDeleteNotification = (notification: WhitelabelNotification) => {
    setNotificationToDelete(notification);
    setDeleteDialogOpen(true);
    handleCloseMenu(notification.id);
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    notificationId: string
  ) => {
    setMenuAnchorEl({ ...menuAnchorEl, [notificationId]: event.currentTarget });
  };

  const handleCloseMenu = (notificationId: string) => {
    setMenuAnchorEl({ ...menuAnchorEl, [notificationId]: null });
  };

  const confirmDelete = async () => {
    if (!notificationToDelete) return;

    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/whitelabel/${whitelabelId}/notifications/${notificationToDelete.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSnackbarMessage("Notification deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setNotificationToDelete(null);
      loadNotifications();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || "Failed to delete notification";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setNotificationToDelete(null);
    }
  };

  const handleCreateNotification = () => {
    setSelectedNotification(null);
    setEditDialogOpen(true);
  };

  const getNotificationTypeColor = (
    type: string
  ): "default" | "primary" | "warning" | "error" | "info" => {
    switch (type) {
      case "warning":
        return "warning";
      case "error":
        return "error";
      case "success":
        return "primary";
      case "announcement":
        return "primary";
      case "feature":
        return "info";
      default:
        return "default";
    }
  };

  const getPriorityColor = (
    priority: string
  ): "default" | "primary" | "warning" | "error" => {
    switch (priority) {
      case "high":
        return "error";
      case "normal":
        return "primary";
      default:
        return "default";
    }
  };

  const isNotificationActive = (notification: WhitelabelNotification) => {
    const now = new Date();
    if (notification.deleted_date) return false;
    if (notification.publishAt && new Date(notification.publishAt) > now)
      return false;
    if (notification.expiresAt && new Date(notification.expiresAt) < now)
      return false;
    return true;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleString();
  };

  if (loading && notifications.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading notifications...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6">Notifications</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateNotification}
        >
          Create Notification
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Create notifications that will be shown to all users on your whitelabel
        instance. Use notifications to announce new features, scheduled
        maintenance, or important updates.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {notifications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Campaign sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Notifications Yet
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Create your first notification to communicate with users on your
            whitelabel instance.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateNotification}
          >
            Create Notification
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Publish At</TableCell>
                <TableCell>Expires At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notifications.map((notification) => {
                const isActive = isNotificationActive(notification);
                return (
                  <TableRow
                    key={notification.id}
                    sx={{ opacity: isActive ? 1 : 0.6 }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {notification.title}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 250 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {notification.message}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={notification.notificationType}
                        size="small"
                        color={getNotificationTypeColor(
                          notification.notificationType
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={notification.priority}
                        size="small"
                        color={getPriorityColor(notification.priority)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {isActive ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Active"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          icon={<Cancel />}
                          label="Inactive"
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {notification.publishAt
                          ? formatDate(notification.publishAt)
                          : "Immediately"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {notification.expiresAt
                          ? formatDate(notification.expiresAt)
                          : "Never"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenMenu(e, notification.id)}
                      >
                        <MoreVert />
                      </IconButton>
                      <Menu
                        anchorEl={menuAnchorEl[notification.id]}
                        open={Boolean(menuAnchorEl[notification.id])}
                        onClose={() => handleCloseMenu(notification.id)}
                      >
                        <MenuItem
                          onClick={() => handleEditNotification(notification)}
                        >
                          <Edit sx={{ mr: 1 }} fontSize="small" />
                          Edit
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleDeleteNotification(notification)}
                        >
                          <Delete sx={{ mr: 1 }} fontSize="small" />
                          Delete
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the notification "
            {notificationToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Edit Notification Dialog */}
      <NotificationEditorDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedNotification(null);
        }}
        notification={selectedNotification}
        whitelabelId={whitelabelId}
        getToken={getToken}
        onSave={() => {
          setSnackbarMessage(
            selectedNotification
              ? "Notification updated successfully!"
              : "Notification created successfully!"
          );
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          loadNotifications();
        }}
      />
    </Box>
  );
}
